import type { ClaudeTool } from '@sazeo/claude';
import type { ComponentType } from 'react';
import type { AddonManifest } from './manifest';

/**
 * Code side of an installed addon — the concrete implementations that the
 * manifest references by name. Keys in `components` and `handlers` must match
 * the string identifiers declared in `manifest.extension_points` and
 * `manifest.api.endpoints[].handler`.
 *
 * Trusted-code model (spec D3): addon code imports workspace packages
 * directly, no WASM sandbox. Scope enforcement happens at call time via
 * `@sazeo/claude` scope checks and RLS at the data layer.
 */
export interface AddonCode {
  /** Component name → React component. Referenced by manifest.extension_points. */
  components: Record<string, ComponentType<Record<string, unknown>>>;
  /** Handler name → async function. Referenced by manifest.api.endpoints + inline_actions. */
  handlers: Record<string, (...args: unknown[]) => Promise<unknown>>;
  /**
   * Claude tool implementations keyed by `manifest.claude_tools[].name`.
   * The mount step calls `registerTool()` for each entry that survives
   * the tenant's scope filter.
   */
  claudeTools?: ClaudeTool<unknown, unknown>[];
}

interface RegistryEntry {
  manifest: AddonManifest;
  code: AddonCode;
  /** tenants that have this addon enabled. */
  enabledTenants: Set<string>;
}

/**
 * Per-runtime registry of installed addons. One instance per app boot; the
 * host shell constructs it at startup and hands it to `mountExtensionPoints`.
 */
export class AddonRegistry {
  private readonly entries = new Map<string, RegistryEntry>();

  /**
   * Names of Claude tools that the last `mountExtensionPoints` call
   * registered with `@sazeo/claude`. Tracked on the registry so
   * `unmountExtensionPoints(registry)` is self-sufficient.
   */
  private mountedClaudeToolNames: string[] = [];

  /** Internal — set by `mountExtensionPoints` to record what it registered. */
  __setMountedClaudeToolNames(names: readonly string[]): void {
    this.mountedClaudeToolNames = [...names];
  }

  /** Internal — read by `unmountExtensionPoints` to know what to tear down. */
  __getMountedClaudeToolNames(): readonly string[] {
    return this.mountedClaudeToolNames;
  }

  register(manifest: AddonManifest, code: AddonCode): void {
    if (this.entries.has(manifest.id)) {
      throw new Error(`[addon-runtime] addon "${manifest.id}" is already registered`);
    }
    this.entries.set(manifest.id, {
      manifest,
      code,
      enabledTenants: new Set<string>(),
    });
  }

  /**
   * Drop an addon from the registry entirely. Does NOT unregister Claude
   * tools — use `unmountExtensionPoints` for that (it knows which tools
   * were mounted per tenant).
   */
  unregister(addonId: string): void {
    this.entries.delete(addonId);
  }

  enableForTenant(tenantId: string, addonId: string): void {
    const entry = this.entries.get(addonId);
    if (!entry) {
      throw new Error(
        `[addon-runtime] cannot enable unknown addon "${addonId}" for tenant "${tenantId}"`,
      );
    }
    entry.enabledTenants.add(tenantId);
  }

  disableForTenant(tenantId: string, addonId: string): void {
    const entry = this.entries.get(addonId);
    if (!entry) return;
    entry.enabledTenants.delete(tenantId);
  }

  isEnabled(tenantId: string, addonId: string): boolean {
    const entry = this.entries.get(addonId);
    if (!entry) return false;
    return entry.enabledTenants.has(tenantId);
  }

  listEnabledForTenant(tenantId: string): AddonManifest[] {
    const result: AddonManifest[] = [];
    for (const entry of this.entries.values()) {
      if (entry.enabledTenants.has(tenantId)) {
        result.push(entry.manifest);
      }
    }
    return result;
  }

  /** Internal — used by mount to pair manifest with code. */
  getEntry(addonId: string): RegistryEntry | undefined {
    return this.entries.get(addonId);
  }

  getManifest(addonId: string): AddonManifest | undefined {
    return this.entries.get(addonId)?.manifest;
  }

  /** Test/reset helper — not part of the production contract. */
  __reset(): void {
    this.entries.clear();
  }
}
